import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { worldMapPath } from './worldMapPath';

/* ── Cities — hubs: Bogotá, Lima, New York, Singapur ── */
const cities = [
  { id: 'bog', name: 'Bogotá', x: 255, y: 268, primary: true },
  { id: 'nyc', name: 'New York', x: 282, y: 192, primary: true },
  { id: 'mia', name: 'Miami', x: 258, y: 218 },
  { id: 'mex', name: 'México DF', x: 205, y: 238 },
  { id: 'sao', name: 'São Paulo', x: 330, y: 340 },
  { id: 'bue', name: 'Buenos Aires', x: 305, y: 378 },
  { id: 'san', name: 'Santiago', x: 280, y: 373 },
  { id: 'lim', name: 'Lima', x: 250, y: 305, primary: true },
  { id: 'lon', name: 'Londres', x: 475, y: 165 },
  { id: 'mad', name: 'Madrid', x: 465, y: 193 },
  { id: 'fra', name: 'Frankfurt', x: 502, y: 168 },
  { id: 'dub', name: 'Dubai', x: 622, y: 235 },
  { id: 'sin', name: 'Singapur', x: 745, y: 280, primary: true },
  { id: 'tok', name: 'Tokio', x: 838, y: 200 },
  { id: 'syd', name: 'Sídney', x: 865, y: 378 },
  { id: 'lag', name: 'Lagos', x: 498, y: 270 },
];

/* ── Routes ── */
const routes = [
  // Bogotá hub
  { from: 'bog', to: 'nyc', delay: 0 },
  { from: 'bog', to: 'mia', delay: 0.8 },
  { from: 'bog', to: 'mex', delay: 1.5 },
  { from: 'bog', to: 'sao', delay: 0.4 },
  { from: 'bog', to: 'lim', delay: 2.0 },
  { from: 'bog', to: 'bue', delay: 1.2 },
  { from: 'bog', to: 'lon', delay: 0.6 },
  { from: 'bog', to: 'mad', delay: 1.8 },
  // Lima hub
  { from: 'lim', to: 'san', delay: 0.3 },
  { from: 'lim', to: 'sao', delay: 1.0 },
  { from: 'lim', to: 'mex', delay: 1.7 },
  { from: 'lim', to: 'mad', delay: 2.3 },
  { from: 'lim', to: 'bue', delay: 0.9 },
  // New York hub
  { from: 'nyc', to: 'lon', delay: 0.5 },
  { from: 'nyc', to: 'fra', delay: 1.3 },
  { from: 'nyc', to: 'mia', delay: 0.2 },
  { from: 'nyc', to: 'mex', delay: 1.8 },
  { from: 'nyc', to: 'dub', delay: 2.1 },
  { from: 'nyc', to: 'tok', delay: 2.6 },
  // Singapur hub
  { from: 'sin', to: 'tok', delay: 0.4 },
  { from: 'sin', to: 'syd', delay: 0.9 },
  { from: 'sin', to: 'dub', delay: 1.5 },
  { from: 'sin', to: 'lon', delay: 2.0 },
  { from: 'sin', to: 'fra', delay: 2.4 },
  { from: 'sin', to: 'lag', delay: 1.1 },
  { from: 'sin', to: 'nyc', delay: 2.8 },
  // Cross
  { from: 'sao', to: 'lag', delay: 3.2 },
];

function getCity(id) { return cities.find(c => c.id === id); }

function getArcPath(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2 - dist * 0.3;
  return `M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`;
}

export default function WorldMap() {
  const { t } = useTranslation();
  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-transparent">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full blur-[150px] opacity-[0.06]" style={{ background: 'radial-gradient(ellipse, #D23CB3, #A84397, transparent)' }} />

      <div className="mx-auto min-w-0 max-w-6xl px-4 sm:px-6">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight">
            {t('worldMap.title')} <span className="gradient-text">{t('worldMap.titleHighlight')}</span>
          </h2>
          <p className="text-on-surface-variant/60 text-sm mt-2">{t('worldMap.subtitle')}</p>
        </motion.div>

        <motion.div
          className="relative min-w-0"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.2 }}
        >
          <svg viewBox="0 0 1000 500" className="h-auto w-full max-w-full" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#A84397" />
                <stop offset="100%" stopColor="#D23CB3" />
              </linearGradient>
              <linearGradient id="landFill" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A84397" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#D23CB3" stopOpacity="0.08" />
              </linearGradient>
              <filter id="dotGlow">
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="hubGlowF">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <radialGradient id="hubRadial">
                <stop offset="0%" stopColor="#D23CB3" stopOpacity="0.6" />
                <stop offset="50%" stopColor="#A84397" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#A84397" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* ── Subtle grid ── */}
            {[0,1,2,3,4,5,6].map(i => (
              <line key={`h${i}`} x1="30" y1={70 + i * 60} x2="970" y2={70 + i * 60} stroke="rgba(168,67,151,0.04)" strokeWidth="0.5" />
            ))}
            {[0,1,2,3,4,5,6,7,8,9,10].map(i => (
              <line key={`v${i}`} x1={80 + i * 80} y1="40" x2={80 + i * 80} y2="460" stroke="rgba(168,67,151,0.04)" strokeWidth="0.5" />
            ))}

            {/* ── Real world map (detailed paths) ── */}
            <motion.path
              d={worldMapPath}
              fill="url(#landFill)"
              stroke="rgba(168,67,151,0.3)"
              strokeWidth="0.6"
              strokeLinejoin="round"
              fillRule="evenodd"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5 }}
            />

            {/* ── Payment routes ── */}
            {routes.map((route, i) => {
              const from = getCity(route.from);
              const to = getCity(route.to);
              const path = getArcPath(from, to);
              return (
                <g key={i}>
                  <path d={path} stroke="rgba(168,67,151,0.05)" strokeWidth="0.8" fill="none" />
                  <motion.circle r="2" fill="#D23CB3" filter="url(#dotGlow)">
                    <animateMotion dur={`${3 + (i % 3)}s`} repeatCount="indefinite" begin={`${route.delay}s`} path={path} />
                  </motion.circle>
                  <motion.path
                    d={path}
                    stroke="url(#routeGrad)"
                    strokeWidth="0.8"
                    fill="none"
                    strokeLinecap="round"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: [0, 0.35, 0.15] }}
                    viewport={{ once: true }}
                    transition={{ duration: 2, delay: route.delay * 0.35, ease: 'easeInOut' }}
                  />
                </g>
              );
            })}

            {/* ── Cities ── */}
            {cities.map((city) => (
              <g key={city.id}>
                {city.primary && (
                  <>
                    {/* Outer pulse */}
                    <motion.circle
                      cx={city.x} cy={city.y} r="22"
                      fill="url(#hubRadial)"
                      animate={{ r: [18, 28, 18], opacity: [0.4, 0.1, 0.4] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    {/* Inner ring */}
                    <motion.circle
                      cx={city.x} cy={city.y} r="12"
                      fill="none" stroke="#D23CB3" strokeWidth="0.8"
                      animate={{ r: [9, 17, 9], opacity: [0.6, 0.1, 0.6] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    {/* Second ring */}
                    <motion.circle
                      cx={city.x} cy={city.y} r="7"
                      fill="none" stroke="#A84397" strokeWidth="0.5"
                      animate={{ r: [5, 10, 5], opacity: [0.4, 0.08, 0.4] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                    />
                  </>
                )}
                {/* Dot */}
                <motion.circle
                  cx={city.x}
                  cy={city.y}
                  r={city.primary ? 5 : 2.5}
                  fill={city.primary ? '#D23CB3' : '#A84397'}
                  filter={city.primary ? 'url(#hubGlowF)' : 'url(#dotGlow)'}
                  animate={city.primary ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                />
                {/* Label — secondary cities hidden on mobile */}
                {(city.primary || typeof window !== 'undefined') && (
                  <text
                    x={city.x}
                    y={city.y - (city.primary ? 14 : 7)}
                    textAnchor="middle"
                    fill={city.primary ? '#ffffff' : 'rgba(214,193,205,0.45)'}
                    fontSize={city.primary ? '11' : '7.5'}
                    fontFamily="Inter, sans-serif"
                    fontWeight={city.primary ? '800' : '400'}
                    letterSpacing={city.primary ? '1' : '0'}
                    className={city.primary ? '' : 'hidden md:inline'}
                  >
                    {city.name}
                  </text>
                )}
              </g>
            ))}
          </svg>
        </motion.div>
      </div>
    </section>
  );
}
